import { type Person } from 'types';

export interface ThreadedComment {
  id: number;
  content_type: number;
  object_id: number;
  author?: Person;
  body: string;
  parent_comment: number | null;
  is_private: boolean;
  // Backend-computed: false for private comments and for public replies to a
  // private comment (unreachable without seeing the private parent).
  is_publicly_visible: boolean;
  created_at: string;
  updated_at: string;
}

export interface ThreadedCommentNode extends ThreadedComment {
  replies: ThreadedCommentNode[];
}

export const buildThreadedCommentTree = (
  comments: ThreadedComment[]
): ThreadedCommentNode[] => {
  const sorted = [...comments].sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  const nodesById: Record<number, ThreadedCommentNode> = {};
  sorted.forEach((comment) => {
    nodesById[comment.id] = { ...comment, replies: [] };
  });

  const roots: ThreadedCommentNode[] = [];
  sorted.forEach((comment) => {
    const node = nodesById[comment.id]!;
    const parent = comment.parent_comment
      ? nodesById[comment.parent_comment]
      : undefined;
    if (parent) {
      parent.replies.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
};

export const countReplies = (comment: ThreadedCommentNode): number =>
  comment.replies.reduce((total, reply) => total + 1 + countReplies(reply), 0);

export const countPubliclyVisibleComments = (
  comments: ThreadedComment[]
): number => comments.filter((comment) => comment.is_publicly_visible).length;

export const filterPrivateThreadedComments = (
  comments: ThreadedCommentNode[],
  showPrivateComments: boolean
): ThreadedCommentNode[] => {
  if (showPrivateComments) {
    return comments;
  }
  return comments
    .filter((comment) => !comment.is_private)
    .map((comment) => ({
      ...comment,
      replies: filterPrivateThreadedComments(
        comment.replies,
        showPrivateComments
      ),
    }));
};
